import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useViewport } from '../../hooks/useViewport';
import { unifiedApiService } from '../../services/unified';
import { useToast } from '../../contexts/ToastContext';
import type { ResumeWithCandidate } from '../../services/unified/types';

interface CandidateCompareModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidateIds: string[];
  resumes: ResumeWithCandidate[];
}

function CandidateCompareModal({ isOpen, onClose, candidateIds, resumes }: CandidateCompareModalProps) {
  const [candidatesData, setCandidatesData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && candidateIds.length > 0) {
      fetchCandidatesData();
    }
  }, [isOpen, candidateIds]);

  const fetchCandidatesData = async () => {
    setLoading(true);
    try {
      const data = await Promise.all(
        candidateIds.map(async (id) => {
          try {
            return await unifiedApiService.admin.getCandidateDetails(id);
          } catch (error) {
            console.error(`Failed to fetch candidate ${id}:`, error);
            return null;
          }
        })
      );
      setCandidatesData(data.filter(Boolean));
    } catch (err) {
      console.error('Failed to fetch candidate data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4">
      <div className="relative top-4 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-6xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">Compare Candidates</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50">
                    Field
                  </th>
                  {candidatesData.map((_, idx) => (
                    <th key={idx} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Candidate {idx + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">Name</td>
                  {candidatesData.map((candidate, idx) => (
                    <td key={idx} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {candidate.user?.name || 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">Email</td>
                  {candidatesData.map((candidate, idx) => (
                    <td key={idx} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {candidate.user?.email || 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">Phone</td>
                  {candidatesData.map((candidate, idx) => (
                    <td key={idx} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {candidate.profile?.phone || 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">Location</td>
                  {candidatesData.map((candidate, idx) => (
                    <td key={idx} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {candidate.profile?.location || 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 sticky left-0 bg-white">Skills</td>
                  {candidatesData.map((candidate, idx) => (
                    <td key={idx} className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex flex-wrap gap-1">
                        {candidate.profile?.skills && candidate.profile.skills.length > 0 ? (
                          candidate.profile.skills.map((skill: string, skillIdx: number) => (
                            <span key={skillIdx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {skill}
                            </span>
                          ))
                        ) : (
                          'N/A'
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 sticky left-0 bg-white">Experience</td>
                  {candidatesData.map((candidate, idx) => (
                    <td key={idx} className="px-6 py-4 text-sm text-gray-900">
                      {candidate.experiences && candidate.experiences.length > 0 ? (
                        <div className="space-y-2">
                          {candidate.experiences.map((exp: any, expIdx: number) => (
                            <div key={expIdx} className="border-l-2 border-gray-300 pl-2">
                              <div className="font-medium">{exp.title}</div>
                              <div className="text-xs text-gray-600">{exp.company}</div>
                              <div className="text-xs text-gray-500">
                                {exp.start_date} - {exp.end_date || 'Present'}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 sticky left-0 bg-white">Education</td>
                  {candidatesData.map((candidate, idx) => (
                    <td key={idx} className="px-6 py-4 text-sm text-gray-900">
                      {candidate.education && candidate.education.length > 0 ? (
                        <div className="space-y-2">
                          {candidate.education.map((edu: any, eduIdx: number) => (
                            <div key={eduIdx} className="border-l-2 border-gray-300 pl-2">
                              <div className="font-medium">{edu.degree}</div>
                              <div className="text-xs text-gray-600">{edu.institution}</div>
                              <div className="text-xs text-gray-500">
                                {edu.start_date} - {edu.end_date || 'Present'}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">Resume Score</td>
                  {candidatesData.map((candidate, idx) => {
                    const resume = resumes.find(r => r.user_id === candidate.user?.id);
                    return (
                      <td key={idx} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {resume?.score ? `${resume.score}/10` : 'N/A'}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">Applications</td>
                  {candidatesData.map((candidate, idx) => (
                    <td key={idx} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {candidate.applications?.length || 0} applications
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

interface ResumeReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  resumeId: string | null;
}

function ResumeReviewModal({ isOpen, onClose, resumeId }: ResumeReviewModalProps) {
  const { showSuccess, showError: showToastError } = useToast();
  const [reviewData, setReviewData] = useState<any>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && resumeId) {
      fetchReviewData();
      loadPdfPreview();
    }
    
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [isOpen, resumeId]);

  const loadPdfPreview = async () => {
    if (!resumeId) return;
    try {
      const blob = await unifiedApiService.resume.downloadResume(resumeId);
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);
    } catch (err) {
      console.error('Failed to load PDF preview:', err);
    }
  };

  const fetchReviewData = async () => {
    if (!resumeId) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await unifiedApiService.resume.getResumeReview(resumeId);
      setReviewData(data);
    } catch (err) {
      console.error('Failed to fetch review data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load review data');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4">
      <div className="relative top-2 sm:top-5 mx-auto p-4 sm:p-5 border w-full max-w-7xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">Resume Review</h3>
            {reviewData && (
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                Scanned using {reviewData.ai_model || 'AI'} • Score: {reviewData.score ? `${reviewData.score.toFixed(1)}/10` : 'N/A'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 sm:space-x-3">
            {reviewData && (
              <>
                <button
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    try {
                      await unifiedApiService.resume.propagateResumeToProfile(resumeId!);
                      showSuccess('Profile Updated', 'Profile has been updated successfully with resume data.');
                    } catch (err) {
                      console.error('Failed to propagate profile:', err);
                      showToastError('Update Failed', 'Failed to update profile. Please try again.');
                    }
                  }}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Update Profile
                </button>
                <button
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    try {
                      setLoading(true);
                      await unifiedApiService.resume.scanResume(resumeId!);
                      await fetchReviewData(); // Refresh the data
                      showSuccess('Rescan Complete', 'Resume has been rescanned successfully.');
                    } catch (err) {
                      console.error('Failed to rescan resume:', err);
                      showToastError('Rescan Failed', 'Failed to rescan resume. Please try again.');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                >
                  Rescan
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchReviewData}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Retry
            </button>
          </div>
        ) : reviewData ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6" style={{ maxHeight: '70vh' }}>
            {/* Left: Resume Preview - Hidden on mobile, shown on desktop */}
            <div className="hidden lg:block border rounded-lg overflow-hidden bg-gray-50">
              <div className="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
                <h4 className="font-medium text-gray-900">Resume Document</h4>
                {pdfBlobUrl && (
                  <a
                    href={pdfBlobUrl}
                    download={`resume-${resumeId}.pdf`}
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </a>
                )}
              </div>
              <div className="overflow-auto" style={{ height: 'calc(70vh - 48px)' }}>
                {pdfBlobUrl ? (
                  <iframe
                    src={pdfBlobUrl}
                    className="w-full h-full border-0"
                    title="Resume PDF"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                      <p>Loading preview...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Extracted Information */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 border-b">
                <h4 className="font-medium text-gray-900">Extracted Information</h4>
              </div>
              <div className="p-4 overflow-auto" style={{ height: 'calc(70vh - 48px)' }}>
                {reviewData.extracted_data ? (
                  <div className="space-y-6">
                    {/* Name */}
                    {reviewData.extracted_data.name && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <p className="text-gray-900">{reviewData.extracted_data.name}</p>
                      </div>
                    )}

                    {/* Email */}
                    {reviewData.extracted_data.email && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <p className="text-gray-900">{reviewData.extracted_data.email}</p>
                      </div>
                    )}

                    {/* Phone */}
                    {reviewData.extracted_data.phone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                        <p className="text-gray-900">{reviewData.extracted_data.phone}</p>
                      </div>
                    )}

                    {/* Skills */}
                    {reviewData.extracted_data.skills && reviewData.extracted_data.skills.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Skills</label>
                        <div className="flex flex-wrap gap-2">
                          {reviewData.extracted_data.skills.map((skill: string, idx: number) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Experience */}
                    {reviewData.extracted_data.experience && reviewData.extracted_data.experience.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Experience</label>
                        <div className="space-y-4">
                          {reviewData.extracted_data.experience.map((exp: any, idx: number) => (
                            <div key={idx} className="border-l-4 border-blue-500 pl-4 py-2">
                              <h5 className="font-medium text-gray-900">{exp.title}</h5>
                              <p className="text-sm text-gray-600">{exp.company}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {exp.start} - {exp.end || 'Present'}
                              </p>
                              {exp.description && (
                                <p className="text-sm text-gray-700 mt-2">{exp.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Education */}
                    {reviewData.extracted_data.education && reviewData.extracted_data.education.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Education</label>
                        <div className="space-y-4">
                          {reviewData.extracted_data.education.map((edu: any, idx: number) => (
                            <div key={idx} className="border-l-4 border-green-500 pl-4 py-2">
                              <h5 className="font-medium text-gray-900">{edu.degree}</h5>
                              <p className="text-sm text-gray-600">{edu.institution}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {edu.start} - {edu.end || 'Present'}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Raw JSON (collapsible) */}
                    <details className="mt-6">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                        View Raw JSON
                      </summary>
                      <pre className="mt-2 p-4 bg-gray-50 rounded text-xs overflow-auto">
                        {JSON.stringify(reviewData.extracted_data, null, 2)}
                      </pre>
                    </details>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <p>No extracted data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

interface BulkResumeActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedResumes: string[];
  onActionComplete: () => void;
}

function BulkResumeActionModal({ isOpen, onClose, selectedResumes, onActionComplete }: BulkResumeActionModalProps) {
  const [action, setAction] = useState<'approve' | 'reject' | 'pending'>('approve');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual bulk resume action API
      await new Promise(resolve => setTimeout(resolve, 1000)); // Mock API call
      onActionComplete();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to perform bulk action');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
      <div className="relative top-10 sm:top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Bulk Resume Action</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            You have selected {selectedResumes.length} resume(s). What would you like to do?
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="approve"
                    checked={action === 'approve'}
                    onChange={(e) => setAction(e.target.value as 'approve')}
                    className="mr-2"
                  />
                  <span className="text-sm">Approve resumes</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="reject"
                    checked={action === 'reject'}
                    onChange={(e) => setAction(e.target.value as 'reject')}
                    className="mr-2"
                  />
                  <span className="text-sm">Reject resumes</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="pending"
                    checked={action === 'pending'}
                    onChange={(e) => setAction(e.target.value as 'pending')}
                    className="mr-2"
                  />
                  <span className="text-sm">Mark as pending review</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50"
              >
                {loading ? 'Processing...' : `${action === 'approve' ? 'Approve' : action === 'reject' ? 'Reject' : 'Update'} Resumes`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export function AdminResumesPage() {
  const { isMobile } = useViewport();
  const { showSuccess, showError: showToastError } = useToast();
  const [resumes, setResumes] = useState<ResumeWithCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResumes, setSelectedResumes] = useState<string[]>([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedResumeForReview, setSelectedResumeForReview] = useState<string | null>(null);
  const [compareCandidates, setCompareCandidates] = useState<string[]>([]);
  const [scanningResumes, setScanningResumes] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'submitted' | 'processing' | 'scanned' | 'error'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'score' | 'name'>('date');

  useEffect(() => {
    const fetchResumes = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch real resume data from API
        const resumesData = await unifiedApiService.resume.getAdminResumes({
          status: statusFilter !== 'all' ? statusFilter : undefined,
          sort_by: sortBy === 'date' ? 'submitted_at' : sortBy === 'score' ? 'score' : 'candidate_name',
          sort_order: 'desc',
        });
        
        setResumes(resumesData);
      } catch (err) {
        console.error('Failed to fetch resumes:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch resumes');
      } finally {
        setLoading(false);
      }
    };

    fetchResumes();
  }, [statusFilter, sortBy]);

  const handleSelectResume = (resumeId: string) => {
    setSelectedResumes(prev => 
      prev.includes(resumeId) 
        ? prev.filter(id => id !== resumeId)
        : [...prev, resumeId]
    );
  };

  const handleSelectAll = () => {
    const filteredResumes = getFilteredResumes();
    if (selectedResumes.length === filteredResumes.length) {
      setSelectedResumes([]);
    } else {
      setSelectedResumes(filteredResumes.map(resume => resume.id));
    }
  };

  const handleBulkActionComplete = () => {
    setSelectedResumes([]);
    // Refresh resumes list
    window.location.reload();
  };

  const getFilteredResumes = () => {
    let filtered = resumes.filter(resume => {
      const matchesSearch = resume.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           resume.candidate_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           resume.filename.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || resume.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });

    // Sort resumes
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'score':
          return (b.score || 0) - (a.score || 0);
        case 'name':
          return (a.candidate_name || '').localeCompare(b.candidate_name || '');
        case 'date':
        default:
          return new Date(b.submitted_at || 0).getTime() - new Date(a.submitted_at || 0).getTime();
      }
    });

    return filtered;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scanned':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'submitted':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getScoreColor = (score?: number) => {
    if (!score) return 'text-gray-400';
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const filteredResumes = getFilteredResumes();

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Resume Review</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
              Review candidate resumes with AI analysis.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {compareCandidates.length > 0 && (
              <button
                onClick={() => setShowCompareModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-md text-sm font-medium min-h-[44px]"
              >
                Compare ({compareCandidates.length})
              </button>
            )}
            {selectedResumes.length > 0 && (
              <button
                onClick={() => setShowBulkModal(true)}
                className="bg-orange-600 hover:bg-orange-700 text-white px-3 sm:px-4 py-2 rounded-md text-sm font-medium min-h-[44px]"
              >
                Bulk ({selectedResumes.length})
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-4 sm:mb-6">
          <div className="space-y-3 sm:space-y-0 sm:flex sm:flex-row sm:gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by name, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
              />
            </div>
            <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="w-full sm:w-auto border border-gray-300 rounded-md px-3 py-2.5 focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px] text-sm"
              >
                <option value="all">All Status</option>
                <option value="submitted">Submitted</option>
                <option value="processing">Processing</option>
                <option value="scanned">Scanned</option>
                <option value="error">Error</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="w-full sm:w-auto border border-gray-300 rounded-md px-3 py-2.5 focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px] text-sm"
              >
                <option value="date">By Date</option>
                <option value="score">By Score</option>
                <option value="name">By Name</option>
              </select>
            </div>
          </div>
        </div>

        {/* Resumes Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedResumes.length === filteredResumes.length && filteredResumes.length > 0}
                onChange={handleSelectAll}
                className="mr-3"
              />
              <span className="text-sm font-medium text-gray-700">
                Select All ({filteredResumes.length} resumes)
              </span>
            </div>
          </div>
          
          <ul className="divide-y divide-gray-200">
            {filteredResumes.map((resume) => (
              <li key={resume.id}>
                {/* Mobile Layout */}
                {isMobile ? (
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedResumes.includes(resume.id)}
                        onChange={() => handleSelectResume(resume.id)}
                        className="mt-1 min-w-[20px] min-h-[20px]"
                      />
                      <div className="flex-1 min-w-0">
                        {/* Name and status */}
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="text-base font-medium text-gray-900 truncate max-w-[180px]">
                            {resume.candidate_name || 'Unknown'}
                          </h3>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(resume.status)}`}>
                            {resume.status}
                          </span>
                        </div>
                        
                        {/* Score */}
                        {resume.score && (
                          <span className={`text-sm font-medium ${getScoreColor(resume.score)}`}>
                            Score: {resume.score}/10
                          </span>
                        )}
                        
                        {/* Meta info */}
                        <div className="text-xs text-gray-500 mt-1 mb-3">
                          <p className="truncate">{resume.candidate_email}</p>
                          <p className="truncate">{resume.filename}</p>
                          <p>{new Date(resume.submitted_at || Date.now()).toLocaleDateString()}</p>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-wrap gap-2">
                          <Link
                            to={`/admin/candidates/${resume.user_id}`}
                            className="px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-md min-h-[36px] flex items-center"
                          >
                            Profile
                          </Link>
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              if (resume.status === 'scanned') {
                                setSelectedResumeForReview(resume.id);
                                setShowReviewModal(true);
                              } else {
                                setScanningResumes(prev => new Set(prev).add(resume.id));
                                try {
                                  const result = await unifiedApiService.resume.scanResume(resume.id);
                                  showSuccess('Scan Complete', 'Resume has been scanned successfully.');
                                  setResumes(prevResumes => 
                                    prevResumes.map(r => 
                                      r.id === resume.id 
                                        ? { ...r, status: 'scanned', score: result.score }
                                        : r
                                    )
                                  );
                                } catch (err) {
                                  console.error('Failed to scan resume:', err);
                                  showToastError('Scan Failed', 'Failed to start scanning.');
                                } finally {
                                  setScanningResumes(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(resume.id);
                                    return newSet;
                                  });
                                }
                              }
                            }}
                            disabled={scanningResumes.has(resume.id)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md min-h-[36px] ${
                              scanningResumes.has(resume.id)
                                ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                                : resume.status === 'scanned'
                                ? 'text-blue-600 bg-blue-50'
                                : 'text-purple-600 bg-purple-50'
                            }`}
                          >
                            {scanningResumes.has(resume.id) 
                              ? 'Scanning...' 
                              : resume.status === 'scanned' 
                              ? 'Review' 
                              : 'Scan AI'}
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                const blob = await unifiedApiService.resume.downloadResume(resume.id);
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = resume.filename || `resume-${resume.id}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                              } catch (err) {
                                console.error('Failed to download resume:', err);
                                showToastError('Download Failed', 'Failed to download resume.');
                              }
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 rounded-md min-h-[36px]"
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Desktop Layout */
                  <div className="px-4 py-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedResumes.includes(resume.id)}
                        onChange={() => handleSelectResume(resume.id)}
                        className="mr-4"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <h3 className="text-lg font-medium text-gray-900">
                                {resume.candidate_name || 'Unknown Candidate'}
                              </h3>
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(resume.status)}`}>
                                {resume.status}
                              </span>
                              {resume.score && (
                                <span className={`ml-2 text-sm font-medium ${getScoreColor(resume.score)}`}>
                                  Score: {resume.score}/10
                                </span>
                              )}
                            </div>
                            <div className="mt-1 flex items-center text-sm text-gray-500">
                              <span>{resume.candidate_email}</span>
                              <span className="mx-2">•</span>
                              <span>{resume.filename}</span>
                              <span className="mx-2">•</span>
                              <span>Submitted {new Date(resume.submitted_at || Date.now()).toLocaleDateString()}</span>
                              <span className="mx-2">•</span>
                              {resume.status === 'scanned' ? (
                                <span className="text-xs text-green-600 font-medium">✓ Scanned using GPT-5</span>
                              ) : (
                                <span className="text-xs text-gray-400 font-medium">Not scanned</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center space-x-3 ml-6">
                            <Link
                              to={`/admin/candidates/${resume.user_id}`}
                              className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                            >
                              View Profile
                            </Link>
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                
                                if (resume.status === 'scanned') {
                                  setSelectedResumeForReview(resume.id);
                                  setShowReviewModal(true);
                                } else {
                                  setScanningResumes(prev => new Set(prev).add(resume.id));
                                  try {
                                    const result = await unifiedApiService.resume.scanResume(resume.id);
                                    showSuccess('Scan Complete', 'Resume has been scanned successfully.');
                                    setResumes(prevResumes => 
                                      prevResumes.map(r => 
                                        r.id === resume.id 
                                          ? { ...r, status: 'scanned', score: result.score }
                                          : r
                                      )
                                    );
                                  } catch (err) {
                                    console.error('Failed to scan resume:', err);
                                    showToastError('Scan Failed', 'Failed to start scanning. Please try again.');
                                  } finally {
                                    setScanningResumes(prev => {
                                      const newSet = new Set(prev);
                                      newSet.delete(resume.id);
                                      return newSet;
                                    });
                                  }
                                }
                              }}
                              disabled={scanningResumes.has(resume.id)}
                              className={`text-sm font-medium ${
                                scanningResumes.has(resume.id)
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : resume.status === 'scanned'
                                  ? 'text-blue-600 hover:text-blue-900'
                                  : 'text-purple-600 hover:text-purple-900'
                              }`}
                            >
                              {scanningResumes.has(resume.id) 
                                ? 'Scanning...' 
                                : resume.status === 'scanned' 
                                ? 'Review' 
                                : 'Scan with AI'}
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const blob = await unifiedApiService.resume.downloadResume(resume.id);
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = resume.filename || `resume-${resume.id}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                } catch (err) {
                                  console.error('Failed to download resume:', err);
                                  showToastError('Download Failed', 'Failed to download resume. Please try again.');
                                }
                              }}
                              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                            >
                              Download
                            </button>
                            <button
                              onClick={() => {
                                if (compareCandidates.includes(resume.user_id)) {
                                  setCompareCandidates(prev => prev.filter(id => id !== resume.user_id));
                                } else if (compareCandidates.length < 3) {
                                  setCompareCandidates(prev => [...prev, resume.user_id]);
                                } else {
                                  showToastError('Comparison Limit', 'You can only compare up to 3 candidates at a time.');
                                }
                              }}
                              className={`text-sm font-medium ${
                                compareCandidates.includes(resume.user_id)
                                  ? 'text-primary-600 hover:text-primary-900'
                                  : 'text-gray-600 hover:text-gray-900'
                              }`}
                            >
                              {compareCandidates.includes(resume.user_id) ? 'Selected' : 'Compare'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
          
          {filteredResumes.length === 0 && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No resumes found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No resumes have been submitted yet.'
                }
              </p>
            </div>
          )}
        </div>

        <BulkResumeActionModal
          isOpen={showBulkModal}
          onClose={() => setShowBulkModal(false)}
          selectedResumes={selectedResumes}
          onActionComplete={handleBulkActionComplete}
        />

        <CandidateCompareModal
          isOpen={showCompareModal}
          onClose={() => {
            setShowCompareModal(false);
            setCompareCandidates([]);
          }}
          candidateIds={compareCandidates}
          resumes={resumes}
        />

        <ResumeReviewModal
          isOpen={showReviewModal}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedResumeForReview(null);
          }}
          resumeId={selectedResumeForReview}
        />
      </div>
    </AdminLayout>
  );
}